import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { LicenseDto } from '@order-pilot/shared';
import { useQuery } from '@tanstack/react-query';
import { AsyncSection } from '@/components/AsyncSection';
import { LicenseStatusBadge } from '@/components/Badge';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { TablePager } from '@/components/TablePager';
import { ConsumedByDevice } from '@/features/licenses/ConsumedByDevice';
import { LicenseFlags } from '@/features/licenses/LicenseFlags';
import {
  fetchLicenses,
  isExpired,
  licenseKeys,
  needsAttention,
} from '@/features/licenses/api';
import { formatDateTime, formatRelative } from '@/lib/format';

const PAGE_SIZE = 20;
const DAY_MS = 24 * 60 * 60 * 1000;

type FilterId = 'all' | 'attention' | 'pending' | 'expiring' | 'revoked';

const FILTERS: ReadonlyArray<{ id: FilterId; label: string; empty: string }> = [
  { id: 'all', label: 'All', empty: 'No licences have been issued yet.' },
  {
    id: 'attention',
    label: 'Needs attention',
    empty: 'Nothing needs attention — no clone signals, clock anomalies, expiries or silent tills.',
  },
  {
    id: 'pending',
    label: 'Awaiting activation',
    empty: 'Every issued code has been redeemed.',
  },
  {
    id: 'expiring',
    label: 'Expiring soon',
    empty: 'Nothing expires in the next 14 days.',
  },
  { id: 'revoked', label: 'Revoked', empty: 'No licences have been revoked.' },
];

function isExpiringSoon(license: LicenseDto): boolean {
  if (license.revokedAt || !license.expiresAt) return false;
  const remaining = new Date(license.expiresAt).getTime() - Date.now();
  return remaining > 0 && remaining <= 14 * DAY_MS;
}

function matches(license: LicenseDto, filter: FilterId): boolean {
  switch (filter) {
    case 'attention':
      return needsAttention(license);
    case 'pending':
      return license.status === 'PENDING' && !license.revokedAt;
    case 'expiring':
      return isExpiringSoon(license);
    case 'revoked':
      return license.revokedAt !== null;
    default:
      return true;
  }
}

/**
 * Ranks a licence by how much it wants a human. The tenant page answers "what
 * does this customer have"; this page answers "what across the fleet needs me",
 * which is the question you actually open the console to ask — so a list sorted
 * by creation date would make you hunt for it.
 */
function urgency(license: LicenseDto): number {
  if (license.revokedAt) return 5;
  if (license.cloneDetectedAt || license.clockAnomalyAt) return 0;
  if (isExpired(license)) return 1;
  if (needsAttention(license)) return 2;
  if (isExpiringSoon(license)) return 3;
  return 4;
}

export function LicensesPage() {
  const [filter, setFilter] = useState<FilterId>('all');
  const [page, setPage] = useState(1);

  const licensesQuery = useQuery({
    queryKey: licenseKeys.list(),
    queryFn: ({ signal }) => fetchLicenses(undefined, signal),
  });

  const rows = useMemo(() => {
    const all = licensesQuery.data ?? [];
    return all
      .filter((license) => matches(license, filter))
      .sort((a, b) => {
        const byUrgency = urgency(a) - urgency(b);
        if (byUrgency !== 0) return byUrgency;
        // Then soonest expiry first; undated (pending) rows last.
        const ax = a.expiresAt ? new Date(a.expiresAt).getTime() : Infinity;
        const bx = b.expiresAt ? new Date(b.expiresAt).getTime() : Infinity;
        return ax - bx;
      });
  }, [licensesQuery.data, filter]);

  // Clamped so deleting rows or switching filters cannot strand you on a page
  // that no longer exists.
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visible = rows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const columns = useMemo<Array<Column<LicenseDto>>>(
    () => [
      {
        key: 'tenant',
        header: 'Tenant',
        cell: (row) =>
          row.tenant ? (
            <Link
              to={`/cp/tenants/${row.tenantId}`}
              className="rounded font-medium text-slate-900 hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
            >
              {row.tenant.name}
            </Link>
          ) : (
            <span className="text-slate-500">—</span>
          ),
      },
      {
        key: 'label',
        header: 'Till',
        cell: (row) => (
          <div>
            <p className="font-medium text-slate-900">{row.label}</p>
            {/* The device that actually activated. Fleet-wide, this is where a
                code sent to the wrong till shows up. */}
            <div className="mt-1">
              <ConsumedByDevice license={row} />
            </div>
            <p className="mt-0.5 font-mono text-xs text-slate-500">
              {row.codePrefix}…
            </p>
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        cell: (row) => <LicenseStatusBadge license={row} />,
      },
      {
        key: 'expires',
        header: 'Expires',
        visibility: 'hidden md:table-cell',
        cell: (row) =>
          row.expiresAt ? (
            <span className="whitespace-nowrap text-slate-700">
              {formatDateTime(row.expiresAt)}
            </span>
          ) : (
            <span className="text-slate-500">
              {row.redeemBy ? `redeem by ${formatDateTime(row.redeemBy)}` : '—'}
            </span>
          ),
      },
      {
        key: 'lastSeen',
        header: 'Last seen',
        visibility: 'hidden lg:table-cell',
        cell: (row) => (
          <span className="whitespace-nowrap text-slate-600">
            {row.status === 'PENDING' ? 'Never' : formatRelative(row.lastRefreshAt)}
          </span>
        ),
      },
      {
        key: 'flags',
        header: 'Flags',
        cell: (row) => <LicenseFlags license={row} />,
      },
    ],
    [],
  );

  const activeFilter = FILTERS.find((f) => f.id === filter) ?? FILTERS[0];
  const attentionCount = (licensesQuery.data ?? []).filter(needsAttention).length;

  return (
    <>
      <PageHeader
        title="Licences"
        description={
          attentionCount > 0
            ? `${attentionCount} licence${attentionCount === 1 ? '' : 's'} need attention.`
            : 'Every licence across every tenant.'
        }
      />

      <SectionCard
        headingId="licenses-table"
        // Reflects the active filter. A heading reading "All licences 0" while
        // a filter is applied says the fleet is empty when it is not.
        title={filter === 'all' ? 'All licences' : activeFilter.label}
        count={rows.length}
        description="Most urgent first — clone signals and clock anomalies, then expired, then expiring soonest."
        action={
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  setFilter(option.id);
                  setPage(1);
                }}
                className={
                  option.id === filter
                    ? 'rounded-full bg-brand-700 px-3 py-1 text-xs font-semibold text-white'
                    : 'rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-200'
                }
              >
                {option.label}
                {option.id === 'attention' && attentionCount > 0
                  ? ` (${attentionCount})`
                  : ''}
              </button>
            ))}
          </div>
        }
      >
        <AsyncSection
          state={licensesQuery}
          skeleton={<TableSkeleton rows={6} columns={6} />}
          isEmpty={() => rows.length === 0}
          emptyTitle="Nothing here"
          emptyDescription={activeFilter.empty}
        >
          {() => (
            <div className="space-y-3">
              <DataTable
                caption="Licences with tenant, till, status, expiry, last contact and warning flags"
                columns={columns}
                rows={visible}
                rowKey={(row) => row.id}
              />
              <TablePager
                page={currentPage}
                pageSize={PAGE_SIZE}
                total={rows.length}
                noun="licence"
                onPageChange={setPage}
              />
            </div>
          )}
        </AsyncSection>
      </SectionCard>
    </>
  );
}
